import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { fetchAllPages } from "../../utils/exportHelpers";
import { Box, Paper, Typography } from "@mui/material";
import { CalendarDate, getLocalTimeZone, today } from "@internationalized/date";
import { useDispatch, useSelector } from "react-redux";
import { fetchAccountPerformance, setMonth, setAccountManagerId, setClientType, setPage, setSortConfig, refreshAccountPerformance, selectLastUpdatedAt, selectOverallStatus, } from "../../redux/accountPerformanceSlice";
import { fetchDesignationWiseUsers, selectDesignationWiseUsers } from "../../redux/clientDataSlice";
import { fetchVisibleClients, hideClients, selectVisibleClients, unhideClient } from "../../redux/accountSummarySlice";
import { selectRefreshLoading } from "../../redux/refreshSlice";
import { useOverlayHoverHandlers } from "../../common_components/hoverOverlay/HoverOverlayProvider";
import PageContainer from "../../common_components/PageContainer";
import PagesCommonHeader from "../../common_components/PagesCommonHeader";
import LastUpdatedInfo from "../../common_components/LastUpdatedInfo";
import { Dropdown, ExportButton, MonthPicker } from "../../common_components/FormInputs";
import CommonTable from "../../common_components/CommonTable";
import Pagination from "../../common_components/Pagination";
import CommonChip from "../../common_components/CommonChip";
import VisibleClientsDropdown from "../../common_components/VisibleClientsDropdown";
import CommonLoader from "../../common_components/CommonLoader";
import { SUMMARY_ROW_POSITIONS, TABLE_TYPES, EXPAND_TRIGGERS } from "../../common_components/tableConstants";
import exportAccountPerformance from "./exportAccountPerformance";
import { truncateName } from "../../helper/truncateName";
import HoverTooltip from "../../common_components/hoverOverlay/HoverTooltip";
import { NETWORK_METADATA_MAP } from "../../utils/networkConstants";
import { AM_DESIGNATIONS, collectUsersByDesignations } from "../../utils/designationFilters";
// ═══════════════ CONSTANTS ═══════════════
const CLIENT_TYPE_OPTIONS = [
    { value: "All", label: "All" },
    { value: "Budget Based", label: "Budget Based" },
    { value: "Roas Based", label: "Roas Based" },
    { value: "CPA Based", label: "CPA Based" },
];
const ICON_MAP = Object.keys(NETWORK_METADATA_MAP).reduce((acc, key) => {
    acc[key] = NETWORK_METADATA_MAP[key].icon;
    return acc;
}, {
    "Facebook Ads": NETWORK_METADATA_MAP.meta.icon,
    "Google Ads": NETWORK_METADATA_MAP.adword.icon,
    "Shopify": NETWORK_METADATA_MAP.shopify.icon,
    "Bing Ads": NETWORK_METADATA_MAP.bing.icon,
    "Google Analytics": NETWORK_METADATA_MAP.ga.icon,
    "others": "/assets/others.svg",
});
const MC_LABELS = {
    meta: "Meta Ads",
    adword: "Google Ads",
    ga: "Google Analytics",
    shopify: "Shopify",
    "Facebook Ads": "Meta Ads",
    "Google Ads": "Google Ads",
    "Shopify": "Shopify",
    "Bing Ads": "Bing Ads",
    "Google Analytics": "Google Analytics",
    "others": "Others",
};
const PATH_MAP = {
    client: "name", am: "am", mc: "mc",
    revenueGoal: "revenue.goal", revenueMtd: "revenue.mtd", revenueVariance: "revenue.variance_percent",
    roasGoal: "roas.goal", roasMtd: "roas.mtd", roasVariance: "roas.variance_percent",
    commission: "commission.baseline",
};
const STATUS_MAP = {
    completed: "Completed", not_found: "Completed",
    pending: "In process", in_progress: "In process", failed: "Failed",
};
const columns = [
    { id: "client", label: "Client", width: "196px", align: "left", sortable: true },
    { id: "am", label: "AM", width: "80px", align: "center", sortable: false },
    { id: "mc", label: "MC", width: "80px", align: "center", sortable: false },
    {
        id: "revenueGroup", label: "Revenue", align: "center", children: [
            { id: "revenueGoal", label: "Goal", width: "120px", align: "center", sortable: true },
            { id: "revenueMtd", label: "MTD", width: "120px", align: "center", sortable: true },
            { id: "revenueVariance", label: "VAR", width: "120px", align: "center", sortable: true },
        ]
    },
    {
        id: "roasGroup", label: "ROAS", align: "center", children: [
            { id: "roasGoal", label: "Goal", width: "100px", align: "center", sortable: true },
            { id: "roasMtd", label: "MTD", width: "100px", align: "center", sortable: true },
            { id: "roasVariance", label: "VAR", width: "100px", align: "center", sortable: true },
        ]
    },
    {
        id: "commissionGroup", label: "Commission", align: "center", children: [
            { id: "commission", label: "Baseline", width: "120px", align: "right", sortable: true },
        ]
    },
];
const tableContainerSx = {
    border: "1px solid #EAECF0", borderRadius: "8px", overflow: "hidden",
    "& table": { borderCollapse: "separate", borderSpacing: 0 },
    "& th, & td": { borderRight: "1px solid #EAECF0", borderBottom: "1px solid #EAECF0" },
    "& tr > :first-of-type": { borderLeft: "1px solid #EAECF0" },
    "& thead tr:first-of-type th": { borderTop: "1px solid #EAECF0" },
    "& thead th, & thead tr:nth-of-type(1) th, & thead tr:nth-of-type(2) th": { background: "#F9FAFB", position: "sticky", zIndex: 6 },
    "& thead tr:nth-of-type(2) th": { top: "45px", zIndex: 5 },
    "& tbody tr:last-of-type td": { backgroundColor: "#F9FAFB", fontWeight: 600, color: "#101828" },
    "& tfoot td, & tfoot th": { backgroundColor: "#F9FAFB", fontWeight: 600 },
};
// ═══════════════ HELPERS ═══════════════
const getDefaultMonth = () => {
    const now = today(getLocalTimeZone());
    return `${now.year}-${String(now.month).padStart(2, "0")}`;
};
const getValue = (row, columnId) => (PATH_MAP[columnId] || columnId).split(".").reduce((obj, key) => obj?.[key], row);
const splitAmNames = (am) => !am || am.trim() === "" || am === "NA" ? ["NA"] : am.split(",").map(s => s.trim()).filter(Boolean);
const getInitials = (name = "") => name.trim().split(/\s+/).filter(Boolean).map(n => n[0]).join("").toUpperCase();
// ═══════════════ SUB-COMPONENTS ═══════════════
const VarianceBadge = React.memo(({ value }) => {
    const isPos = value >= 0;
    return (_jsxs(Box, { sx: {
            backgroundColor: isPos ? "#ecfdf3" : "#fef3f2",
            borderRadius: "16px", px: 1, py: 0.5,
            display: "inline-flex", alignItems: "center", gap: 0.5
        }, children: [_jsxs(Typography, { sx: { fontFamily: "Inter", fontWeight: 500, fontSize: "12px", color: isPos ? "#027a48" : "#b42318" }, children: [Math.abs(value).toLocaleString("en-US", { maximumFractionDigits: 0 }), "%"] }), _jsx("img", { src: isPos ? "/assets/trend-up.svg" : "/assets/trending-down.svg", alt: "" })] }));
});
const CurrencyText = React.memo(({ value, bold }) => (_jsx(Box, { sx: { px: 1, textAlign: "left" }, children: _jsxs(Typography, { sx: {
            fontFamily: "Inter", fontWeight: bold ? 600 : 400,
            color: bold ? "#181d27" : "#27272a", whiteSpace: "nowrap", fontSize: "14px"
        }, children: [_jsx("span", { style: { color: bold ? "#a4a7ae" : "#a1a1aa" }, children: "$ " }), value?.toLocaleString()] }) })));
// ═══════════════ MAIN COMPONENT ═══════════════
export default function AccountPerformance() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const overlayHover = useOverlayHoverHandlers();
    // ⚡ Refs
    const initialized = useRef(false);
    const skipNextFetch = useRef(true);
    const permissions = useSelector((state) => state.auth.permissions);
    const isGrantScope = permissions?.account_performance?.client_scope === "all";
    // ═══════════════ SELECTORS ═══════════════
    const { clients, totalSummary, pagination, loading, month, accountManagerId, clientType, page, limit, sortBy, sortOrder, cache } = useSelector((state) => state.accountPerformance);
    const overallStatus = useSelector(selectOverallStatus);
    const refreshLoading = useSelector(selectRefreshLoading);
    const lastUpdatedAt = useSelector(selectLastUpdatedAt);
    const designationWiseUsers = useSelector(selectDesignationWiseUsers);
    const visibleClients = useSelector(selectVisibleClients);
    // ═══════════════ LOCAL STATE ═══════════════
    const [visibleClientsList, setVisibleClientsList] = useState([]);
    const [exporting, setExporting] = useState(false);
    const [toggleLoading, setToggleLoading] = useState(false);
    // ═══════════════ MEMOIZED VALUES ═══════════════
    const statusText = useMemo(() => STATUS_MAP[overallStatus || ""] || "Checking...", [overallStatus]);
    const accountManagers = useMemo(() => collectUsersByDesignations(designationWiseUsers?.designationMap, AM_DESIGNATIONS), [designationWiseUsers]);
    const amOptions = useMemo(() => [
        { value: "", label: "All" },
        ...accountManagers.map((u) => ({ value: u._id, label: `${u.first_name} ${u.last_name}` })),
    ], [accountManagers]);
    const selectedAm = useMemo(() => accountManagers.find((u) => u._id === accountManagerId), [accountManagerId, accountManagers]);
    const selectedCalendarDate = useMemo(() => {
        if (!month)
            return null;
        const [y, m] = month.split("-").map(Number);
        return new CalendarDate(y, m, 1);
    }, [month]);
    // ═══════════════ ⚡ SINGLE INIT (ONE TIME) ═══════════════
    useEffect(() => {
        if (initialized.current)
            return;
        initialized.current = true;
        const currentMonth = month || getDefaultMonth();
        if (!month)
            dispatch(setMonth(currentMonth));
        // ⚡ PARALLEL - All 3 APIs at once
        Promise.all([
            dispatch(fetchDesignationWiseUsers()),
            dispatch(fetchVisibleClients({ moduleKey: "account_performance", month: currentMonth })),
            dispatch(fetchAccountPerformance({
                month: currentMonth,
                account_manager_id: accountManagerId || undefined,
                client_type: clientType || "All",
                sortBy,
                sortOrder,
                page,
                limit,
            })),
        ]);
    }, []);
    // ═══════════════ FILTER CHANGE (Skip Initial) ═══════════════
    useEffect(() => {
        if (skipNextFetch.current) {
            skipNextFetch.current = false;
            return;
        }
        if (!month)
            return;
        dispatch(fetchAccountPerformance({
            month,
            account_manager_id: accountManagerId || undefined,
            client_type: clientType || "All",
            sortBy,
            sortOrder,
            page, limit
        }));
        dispatch(fetchVisibleClients({ moduleKey: "account_performance", month }));
    }, [month, accountManagerId, clientType, page, limit, sortBy, sortOrder]);
    // ═══════════════ SYNC VISIBLE CLIENTS ═══════════════
    useEffect(() => {
        if (visibleClients?.length) {
            setVisibleClientsList(visibleClients.map((c) => ({ id: c._id, name: c.name, visible: c.visible })));
        }
    }, [visibleClients]);
    // ═══════════════ HANDLERS ═══════════════
    const refetchPerformance = useCallback(() => {
        if (!month)
            return;
        return dispatch(fetchAccountPerformance({
            month,
            account_manager_id: accountManagerId || undefined,
            client_type: clientType || "All",
            sortBy,
            sortOrder,
            page, limit
        }));
    }, [dispatch, month, accountManagerId, clientType, page, limit, sortBy, sortOrder]);
    // ⚡ OPTIMISTIC UI - No refetch visible clients
    const handleClientToggle = useCallback(async (clientId, visible) => {
        setVisibleClientsList(prev => prev.map(c => c.id === clientId ? { ...c, visible } : c));
        setToggleLoading(true);
        try {
            visible
                ? await dispatch(unhideClient({ clientId, moduleKey: "account_performance" })).unwrap()
                : await dispatch(hideClients({ client_ids: [clientId], reason: null, moduleKey: "account_performance" })).unwrap();
            await dispatch(fetchVisibleClients({ moduleKey: "account_performance", month })).unwrap();
            await refetchPerformance();
        }
        catch (e) {
            setVisibleClientsList(prev => prev.map(c => c.id === clientId ? { ...c, visible: !visible } : c));
            console.error(e);
        }
        finally {
            setToggleLoading(false);
        }
    }, [dispatch, refetchPerformance]);
    const handleBulkToggle = useCallback(async (ids, visible) => {
        const idSet = new Set(ids);
        setVisibleClientsList(prev => prev.map(c => idSet.has(c.id) ? { ...c, visible } : c));
        setToggleLoading(true);
        try {
            visible
                ? await dispatch(unhideClient({ client_ids: ids, moduleKey: "account_performance" })).unwrap()
                : await dispatch(hideClients({ client_ids: ids, reason: null, moduleKey: "account_performance" })).unwrap();
            await dispatch(fetchVisibleClients({ moduleKey: "account_performance", month })).unwrap();
            await refetchPerformance();
        }
        catch (e) {
            setVisibleClientsList(prev => prev.map(c => idSet.has(c.id) ? { ...c, visible: !visible } : c));
            console.error(e);
        }
        finally {
            setToggleLoading(false);
        }
    }, [dispatch, refetchPerformance]);
    const filteredClients = useMemo(() => {
        const hiddenNames = new Set(visibleClientsList.filter(c => !c.visible).map(c => c.name.toLowerCase()));
        if (hiddenNames.size === 0)
            return clients;
        return clients.filter(c => !hiddenNames.has((c.name || '').toLowerCase()));
    }, [clients, visibleClientsList]);
    const handleRefresh = useCallback(async () => {
        if (!month || refreshLoading)
            return;
        const clientIds = filteredClients.map((c) => c._id || c.client_id || c.id).filter(Boolean);
        await dispatch(refreshAccountPerformance({
            month,
            clientIds: clientIds.length > 0 ? clientIds : undefined
        })).unwrap();
        refetchPerformance();
    }, [dispatch, month, refetchPerformance, refreshLoading, filteredClients]);
    const handleExport = useCallback(async () => {
        if (!month || exporting)
            return;
        setExporting(true);
        try {
            // Fetch all pages in safe batches instead of one massive request
            let cachedSummary = null;
            const allClients = await fetchAllPages(async (page, limit) => {
                const result = await dispatch(fetchAccountPerformance({
                    month,
                    account_manager_id: accountManagerId || undefined,
                    client_type: clientType || "All",
                    page,
                    limit,
                })).unwrap();
                if (!cachedSummary)
                    cachedSummary = result.total_summary;
                return {
                    items: result.clients || [],
                    totalPages: result.pagination?.total_pages || 1,
                };
            });
            exportAccountPerformance(allClients, cachedSummary, month);
        }
        catch (error) {
            console.error('Export failed:', error);
        }
        finally {
            setExporting(false);
        }
    }, [dispatch, month, accountManagerId, clientType, exporting]);
    // ═══════════════ RENDER HELPERS ═══════════════
    const renderDropdownValue = useCallback((label, value) => (_jsxs("span", { children: [_jsxs("span", { style: { color: "#101828", fontWeight: 500 }, children: [label, ":"] }), " ", _jsx("span", { style: { color: "#667085" }, children: value })] })), []);
    const renderCell = useCallback((row, col) => {
        switch (col.id) {
            case "client": {
                const isBudgetBased = row.client_type?.toLowerCase()?.includes("budget");
                return (_jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 1, px: 1, cursor: "pointer" }, onClick: () => navigate(`/account-performance-report`, { state: { clientId: row._id || row.id || row.client_id, clientName: row.name, month } }), children: [_jsxs(Typography, { sx: { fontWeight: 400, color: "#27272a", fontSize: "14px" }, children: [row.name, isBudgetBased && _jsx("span", { style: { color: "red", marginLeft: "4px" }, children: "*" })] }), _jsx("img", { src: "/assets/link.svg", alt: "link" })] }));
            }
            case "am": {
                const names = splitAmNames(row.am);
                const visible = names.slice(0, 2);
                const extra = names.slice(2);
                return (_jsx(Box, { sx: { display: "flex", justifyContent: "center" }, children: _jsxs(Box, { sx: { display: "flex", position: "relative", zIndex: 1 }, children: [visible.map((name, idx) => (_jsx(CommonChip, { label: getInitials(name), tooltip: name, renderType: "circle", index: idx, className: `circle manager-circle ${idx === 0 ? "first" : ""} ${name === "NA" ? "na" : "ok"}`, ...overlayHover }, `${name}-${idx}`))), extra.length > 0 && (_jsx(CommonChip, { label: `+${extra.length}`, renderType: "circle", className: "circle extra-circle manager-circle", tooltip: _jsx(Box, { sx: { background: "black", borderRadius: "8px", overflow: "hidden", minWidth: 180 }, children: extra.map((n, i) => (_jsx(Box, { sx: {
                                            px: 1, py: 1, fontSize: "14px", textTransform: "capitalize",
                                            borderBottom: i !== extra.length - 1 ? "1px solid rgba(255,255,255,0.5)" : "none"
                                        }, children: n }, `${n}-${i}`))) }), ...overlayHover }))] }) }));
            }
            case "mc": {
                const channels = Array.isArray(row.mc) ? row.mc : (row.mc ? [row.mc] : []);
                if (channels.length === 0) {
                    return (_jsx(Box, { sx: { display: "flex", justifyContent: "center" }, children: _jsx(Box, { sx: {
                                width: 28, height: 28, borderRadius: "50%", backgroundColor: "#f4f4f5",
                                display: "flex", alignItems: "center", justifyContent: "center",
                                fontSize: "12px", fontWeight: 600, color: "#71717a"
                            }, children: "NA" }) }));
                }
                return (_jsx(Box, { sx: { display: "flex", justifyContent: "center", gap: 0.5 }, children: channels.map((channel, index) => {
                        const mcLabel = MC_LABELS[channel] || (channel ? channel.charAt(0).toUpperCase() + channel.slice(1) : "No MC");
                        return (_jsx(HoverTooltip, { title: mcLabel, children: _jsx(Box, { sx: { display: "flex" }, children: ICON_MAP[channel] ? (_jsx("img", { src: ICON_MAP[channel], alt: channel, style: { width: 24, height: 24, objectFit: "contain" } })) : (_jsx(Box, { sx: {
                                        width: 24, height: 24, borderRadius: "50%", backgroundColor: "#f4f4f5",
                                        display: "flex", alignItems: "center", justifyContent: "center",
                                        fontSize: "10px", fontWeight: 600, color: "#71717a"
                                    }, children: "NA" })) }) }, index));
                    }) }));
            }
            case "revenueGoal": return _jsx(CurrencyText, { value: Math.round(row.revenue.goal || 0) });
            case "revenueMtd": return _jsx(CurrencyText, { value: Math.round(row.revenue.mtd || 0) });
            case "revenueVariance": return _jsx(Box, { sx: { display: "flex", justifyContent: "flex-start" }, children: _jsx(VarianceBadge, { value: row.revenue.variance_percent }) });
            case "roasGoal": return _jsx(Box, { sx: { px: 1 }, children: _jsx(Typography, { sx: { fontFamily: "Inter", fontWeight: 400, color: "#27272a", fontSize: "14px" }, children: row.roas.goal?.toFixed(2) }) });
            case "roasMtd": return _jsx(Box, { sx: { px: 1 }, children: _jsx(Typography, { sx: { fontFamily: "Inter", fontWeight: 400, color: "#27272a", fontSize: "14px" }, children: row.roas.mtd?.toFixed(2) }) });
            case "roasVariance": return _jsx(Box, { sx: { display: "flex", justifyContent: "flex-start" }, children: _jsx(VarianceBadge, { value: row.roas.variance_percent }) });
            case "commission": return _jsx(CurrencyText, { value: row.commission.baseline });
            default: return null;
        }
    }, [overlayHover, month]);
    const renderSummaryRow = useCallback((_, col) => {
        if (col.id === "client")
            return _jsx(Box, { sx: { px: 1 }, children: _jsx(Typography, { sx: { fontWeight: 600, color: "#181d27", fontSize: "14px" }, children: "Total Summary" }) });
        if (["am", "mc"].includes(col.id))
            return "";
        if (!totalSummary)
            return "";
        switch (col.id) {
            case "revenueGoal": return _jsx(CurrencyText, { value: Math.round(totalSummary.revenue.goal || 0), bold: true });
            case "revenueMtd": return _jsx(CurrencyText, { value: Math.round(totalSummary.revenue.mtd || 0), bold: true });
            case "revenueVariance": return _jsx(Box, { sx: { display: "flex", justifyContent: "center" }, children: _jsx(VarianceBadge, { value: totalSummary.revenue.variance_percent }) });
            case "roasGoal":
            case "roasMtd":
            case "roasVariance":
            case "commission":
                return _jsx(Box, { sx: { px: 1, textAlign: "center" }, children: _jsx(Typography, { sx: { fontWeight: 600, fontFamily: "Inter", color: "#a4a7ae", fontSize: "14px" }, children: "-" }) });
            default: return "";
        }
    }, [totalSummary]);
    // ═══════════════ COMPUTED ═══════════════
    const isRefreshDisabled = statusText === "In process" || refreshLoading;
    const showPagination = pagination && pagination.total_clients > 0;
    const showSummaryRow = filteredClients.length > 0;
    // ═══════════════ RENDER ═══════════════
    return (_jsx(PageContainer, { children: _jsxs(Paper, { elevation: 0, sx: { display: "flex", flexDirection: "column", gap: "8px" }, children: [_jsx(PagesCommonHeader, { height: "65px", lastUpdatedSection: _jsx(LastUpdatedInfo, { updatedAt: lastUpdatedAt, statusText: statusText, onRefresh: handleRefresh, loading: refreshLoading, moduleName: "account_performance", disabled: isRefreshDisabled, disabledReason: refreshLoading || statusText === "In process"
                            ? "Refresh is working in background. Please wait until its complete."
                            : "" }), rightSection: _jsxs(Box, { display: "flex", gap: 1, alignItems: "center", children: [isGrantScope &&
                                _jsx(Dropdown, { value: accountManagerId, onChange: (v) => dispatch(setAccountManagerId(v)), options: amOptions, placeholder: "AM: All", displayValue: renderDropdownValue("AM", truncateName(selectedAm ? `${selectedAm.first_name} ${selectedAm.last_name}` : "All", 10)), leftIcon: "/assets/userC.svg", size: "sm", fullWidth: false, className: "header-dropdown", searchable: accountManagers.length > 10 }), isGrantScope && (_jsx(Dropdown, { value: clientType, onChange: (v) => dispatch(setClientType(v)), options: CLIENT_TYPE_OPTIONS, placeholder: "Client Type: All", displayValue: renderDropdownValue("Client Type", truncateName(CLIENT_TYPE_OPTIONS.find(t => t.value === clientType)?.label || "All", 6)), leftIcon: "/assets/TypeC.svg", size: "sm", fullWidth: false, className: "header-dropdown" })), _jsx(MonthPicker, { showAllTime: false, value: selectedCalendarDate, onChange: (v) => v !== "ALL_TIME" && dispatch(setMonth(`${v.year}-${String(v.month).padStart(2, "0")}`)), fullWidth: false, className: "header-dropdown" }), isGrantScope && visibleClientsList.length > 0 && (_jsx(VisibleClientsDropdown, { clients: visibleClientsList, onChange: setVisibleClientsList, label: "Visible Client", onToggle: handleClientToggle, onBulkToggle: handleBulkToggle })), _jsx(ExportButton, { loading: exporting, onClick: handleExport })] }) }), _jsxs(Box, { className: "table-container", sx: { ...tableContainerSx, position: "relative" }, children: [_jsx(CommonTable, { columns: columns, rows: filteredClients, renderCell: renderCell, isFirstLoad: loading && !clients.length, sortConfig: { columnId: sortBy, direction: sortOrder }, onSortChange: (id, dir) => dispatch(setSortConfig({ sortBy: id, sortOrder: dir || "asc" })), expandTrigger: EXPAND_TRIGGERS.NONE, enableColumnActions: false, enableColumnReorder: false, enableColumnPinning: false, enableColumnFilters: false, enableColumnVisibility: false, enableSelection: false, enableSummaryRow: showSummaryRow, renderSummaryRow: showSummaryRow ? renderSummaryRow : undefined, summaryRowPosition: SUMMARY_ROW_POSITIONS.BOTTOM, type: TABLE_TYPES.GROUPED, isLoading: false, stickyHeader: true, maxHeight: "calc(100vh - 190px)" }), (loading || toggleLoading) && (_jsx(Box, { sx: { position: "absolute", inset: 0, background: "white", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100 }, children: _jsx(CommonLoader, { className: "table-loader" }) })), showPagination && (_jsx(Box, { className: "user-list-pagination-wrapper", children: _jsx(Pagination, { currentPage: pagination.page, totalPages: pagination.total_pages, onPageChange: (p) => dispatch(setPage(p)), maxVisiblePages: 5, totalEntries: pagination.total_clients, itemsPerPage: pagination.limit, showControls: false }) }))] })] }) }));
}
